<template>
  <div>
    <!-- Ścieżka jak w module zamówień - powrót do listy jest jej częścią. -->
    <div class="flex items-center justify-between">
      <SubiektBreadcrumb in-panel :current="document ? document.tytul : 'Dokument'" />
      <a @click="goBack()" class="cursor-pointer text-sm font-medium text-blue-500 hover:text-blue-700 md:hidden">&larr; Cofnij</a>
      <BridgeStatus :status="bridge" :queued="document && ['WYSLANY', 'W_TRAKCIE'].includes(document.status) ? 1 : 0" />
    </div>

    <div v-if="loading" class="mt-6 space-y-4">
      <div class="h-24 animate-pulse rounded-lg bg-white shadow"></div>
      <div class="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div class="h-96 animate-pulse rounded-lg bg-white shadow lg:col-span-2"></div>
        <div class="h-72 animate-pulse rounded-lg bg-white shadow"></div>
      </div>
    </div>

    <div v-else-if="loadError" class="mt-6 rounded-lg border bg-white px-6 py-12 text-center shadow">
      <ExclamationIcon class="mx-auto h-8 w-8 text-red-400" aria-hidden="true" />
      <p class="mt-2 text-sm text-gray-700">{{ loadError }}</p>
      <button @click="loadDocument()" type="button"
        class="mt-3 inline-flex h-9 items-center rounded-md bg-indigo-100 px-3 text-xs font-semibold text-indigo-600 hover:bg-indigo-200">Spróbuj ponownie</button>
    </div>

    <div v-else-if="document" class="mt-5">
      <!-- NAGŁÓWEK -->
      <div class="rounded-lg border bg-white px-5 py-4 shadow">
        <div class="flex flex-wrap items-start justify-between gap-4">
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <span :class="[typeBadge(document.action), 'inline-flex rounded px-1.5 py-0.5 font-mono text-xs font-semibold']">{{ document.action }}</span>
              <h1 class="text-lg font-semibold text-gray-900">{{ document.tytul }}</h1>
              <span v-tooltip="status.tooltip"
                :class="[status.badge, 'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium']">
                <RefreshIcon v-if="status.spin" class="h-3 w-3 animate-spin" aria-hidden="true" />
                {{ status.text }}
              </span>
            </div>
            <p class="mt-1 text-xs text-gray-500">
              {{ groupLabel }}
              <span class="ml-1 font-mono text-gray-400">{{ document.id }}</span>
            </p>
          </div>

          <div class="flex flex-wrap items-center gap-2">
            <button v-if="canEdit" @click="refreshFromCatalog()" :disabled="refreshing" type="button"
              v-tooltip="'Przelicz dokument z kartoteki produktów (symbole Subiekta)'"
              :class="[refreshing ? 'opacity-50 pointer-events-none' : '', 'transition-all duration-300 inline-flex h-9 items-center gap-x-1.5 rounded-md bg-indigo-100 px-2.5 text-xs font-semibold text-indigo-600 shadow-sm hover:bg-indigo-200']">
              <RefreshIcon :class="[refreshing ? 'animate-spin' : '', '-ml-0.5 h-4 w-4']" aria-hidden="true" />
              {{ refreshing ? 'Odświeżam…' : 'Odśwież z kartoteki' }}
            </button>
            <button v-if="canSend && !document.tylkoPorownanie" @click="sendDocument()"
              :disabled="!sendingEnabled || sending || hasBlockers" type="button"
              v-tooltip="!sendingEnabled ? 'Wysyłka do Subiekta jest wyłączona' : (hasBlockers ? document.blokady.join(' ') : 'Wstaw do kolejki — most wystawi dokument w Subiekcie')"
              :class="[(!sendingEnabled || sending || hasBlockers) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-500',
                       'transition-all duration-300 inline-flex h-9 items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 text-xs font-semibold text-white shadow-sm']">
              <RefreshIcon v-if="sending" class="-ml-0.5 h-4 w-4 animate-spin" aria-hidden="true" />
              <PaperAirplaneIcon v-else class="-ml-0.5 h-4 w-4 transform rotate-90" aria-hidden="true" />
              {{ sending ? 'Wysyłam…' : 'Wyślij do Subiekta' }}
            </button>
          </div>
        </div>
        <!-- Nagłówek w Subiekcie: dokładnie te pola, które pojadą do dokumentu. -->
        <dl class="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-gray-100 pt-4 md:grid-cols-4">
          <div v-for="field in headerFields" :key="field.label" :class="field.long ? 'col-span-2' : ''">
            <dt class="text-xs font-medium uppercase text-gray-500">{{ field.label }}</dt>
            <dd class="mt-0.5 whitespace-pre-line break-words text-sm font-semibold text-gray-900">{{ field.value || '—' }}</dd>
          </div>
        </dl>
      </div>

      <!-- KOMUNIKATY - od najważniejszego. Każdy mówi, co się stało i co zrobić. -->
      <div class="mt-4 space-y-3">
        <div v-if="sendingEnabled === false" class="flex gap-3 rounded-md border border-yellow-200 bg-yellow-50 px-4 py-2.5">
          <ExclamationIcon class="h-5 w-5 flex-shrink-0 text-yellow-500" aria-hidden="true" />
          <p class="text-sm text-yellow-800">Wysyłka do Subiekta jest wyłączona (<span class="font-mono text-xs">Configs/subiekt.enabled</span>) — dokument można sprawdzać, ale nie trafi do kolejki.</p>
        </div>

        <!-- Wystawiony. Cofnięcie to najgroźniejsza operacja panelu - dokument w Subiekcie
             ISTNIEJE, więc ponowna wysyłka bez usunięcia go tam zrobi duplikat. -->
        <div v-if="document.status === 'WYSTAWIONY'" class="rounded-md border border-green-200 bg-green-50 px-4 py-3">
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div class="flex min-w-0 gap-3">
              <CheckCircleIcon class="h-5 w-5 flex-shrink-0 text-green-500" aria-hidden="true" />
              <div class="text-sm text-green-800">
                <p class="font-semibold text-green-900">
                  Wystawiony w Subiekcie<template v-if="document.subiekt && document.subiekt.numer"> jako {{ document.subiekt.numer }}</template>
                </p>
                <p v-if="document.meta && document.meta.potwierdzenieUnix" class="mt-0.5">{{ formatUnix(document.meta.potwierdzenieUnix) }}</p>
              </div>
            </div>
            <button @click="cofnij()" :disabled="savingCofnij" type="button"
              :class="[savingCofnij ? 'opacity-50 pointer-events-none' : 'hover:bg-green-100', 'transition duration-200 inline-flex h-8 items-center gap-1.5 rounded-md bg-white px-3 text-xs font-semibold text-green-800 ring-1 ring-inset ring-green-200']">
              <RefreshIcon v-if="savingCofnij" class="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              Cofnij do szkicu
            </button>
          </div>
          <!-- PZ od dostawcy: faktura zakupu powstaje na jej podstawie automatycznie
               (most wiąże ją z e-fakturą z KSeF). Tu widać, czy już jest. -->
          <div v-if="document.grupa === 'PZ_ZAKUP'" class="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-md bg-white px-4 py-3 text-sm text-gray-700 ring-1 ring-inset ring-green-200">
            <p v-if="document.fakturaZakupu">
              Faktura zakupu do tej PZ:
              <a @click="openDocument(document.fakturaZakupu.id)" class="cursor-pointer font-semibold text-indigo-600 hover:text-indigo-500">otwórz</a>
              — most wystawia ją na podstawie PZ i wiąże z e-fakturą {{ document.numerFaktury }} z KSeF.
            </p>
            <p v-else>Faktura zakupu do tej PZ jeszcze nie powstała.</p>
            <button v-if="!document.fakturaZakupu" @click="createPurchaseInvoice()" :disabled="tworzeFZ" type="button"
              :class="[tworzeFZ ? 'opacity-50 pointer-events-none' : 'hover:bg-indigo-200', 'transition-all duration-300 inline-flex h-8 items-center gap-1.5 rounded-md bg-indigo-100 px-3 text-xs font-semibold text-indigo-600']">
              <RefreshIcon v-if="tworzeFZ" class="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              Wystaw fakturę zakupu
            </button>
          </div>
        </div>

        <!-- W kolejce / w trakcie. -->
        <div v-if="canRevert" class="flex flex-wrap items-start justify-between gap-3 rounded-md border border-blue-200 bg-blue-50 px-4 py-3">
          <div class="flex min-w-0 gap-3">
            <RefreshIcon :class="[document.status === 'W_TRAKCIE' ? 'animate-spin' : '', 'h-5 w-5 flex-shrink-0 text-blue-500']" aria-hidden="true" />
            <div class="text-sm text-blue-800">
              <p class="font-semibold text-blue-900">{{ queueTitle }}</p>
              <p class="mt-0.5">{{ queueText }}</p>
              <p v-if="document.zadanie && document.zadanie.ostatniBlad" class="mt-1 text-xs text-blue-700">Ostatnia próba: {{ document.zadanie.ostatniBlad }}</p>
            </div>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <!-- Dokument wysłany starym mostem nie ma kanału zwrotnego - numer wpisuje człowiek. -->
            <template v-if="isLegacyQueued">
              <input v-model="manualNumber" @keyup.enter="confirmManually()" type="text" placeholder="np. WZ 12/MAG/2026"
                class="h-8 w-44 rounded-md border-blue-200 px-2.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500" />
              <button @click="confirmManually()" :disabled="savingPotwierdz || manualNumber.trim().length === 0" type="button"
                :class="[(savingPotwierdz || manualNumber.trim().length === 0) ? 'opacity-50 pointer-events-none' : 'hover:bg-green-100', 'transition duration-200 inline-flex h-8 items-center gap-1.5 rounded-md bg-green-50 px-3 text-xs font-semibold text-green-800 ring-1 ring-inset ring-green-200']">
                Jest w Subiekcie
              </button>
            </template>
            <button @click="cofnij()" :disabled="savingCofnij" type="button"
              :class="[savingCofnij ? 'opacity-50 pointer-events-none' : 'hover:bg-blue-100', 'transition duration-200 inline-flex h-8 items-center gap-1.5 rounded-md bg-white px-3 text-xs font-semibold text-blue-800 ring-1 ring-inset ring-blue-200']">
              <RefreshIcon v-if="savingCofnij" class="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              {{ document.status === 'WYSLANY' && !isLegacyQueued ? 'Wycofaj z kolejki' : 'Cofnij do szkicu' }}
            </button>
          </div>
        </div>

        <div v-if="document.czekaNa && document.action !== 'FZ' && document.status !== 'WYSTAWIONY'" class="flex gap-3 rounded-md border border-gray-200 bg-white px-4 py-2.5">
          <InformationCircleIcon class="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
          <p class="text-sm text-gray-700">
            Most wystawi ten dokument dopiero po
            <a @click="openDocument(document.czekaNa)" class="cursor-pointer font-semibold text-indigo-600 hover:text-indigo-500">przyjęciu na braki</a>
            — inaczej trafiłby na ten sam brak towaru.
          </p>
        </div>

        <!-- Faktura zakupu: skąd się bierze i co most z nią zrobi. -->
        <div v-if="document.action === 'FZ' && document.naPodstawie" class="flex gap-3 rounded-md border border-gray-200 bg-white px-4 py-2.5">
          <InformationCircleIcon class="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
          <p class="text-sm text-gray-700">
            Faktura zakupu na podstawie
            <a @click="openDocument(document.naPodstawie.docId)" class="cursor-pointer font-semibold text-indigo-600 hover:text-indigo-500">{{ document.naPodstawie.numer }}</a>.
            Most znajdzie w Subiekcie e-fakturę <span class="font-semibold">{{ document.numerFaktury }}</span> z KSeF, sprawdzi, czy wartość
            zgadza się z PZ, i powiąże ją z tą fakturą. Pozycje i ceny pochodzą z PZ.
          </p>
        </div>

        <div v-if="document.blad" class="flex gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3">
          <XCircleIcon class="h-5 w-5 flex-shrink-0 text-red-500" aria-hidden="true" />
          <div class="text-sm text-red-800">
            <p class="font-semibold text-red-900">Ostatnia wysyłka nie przeszła</p>
            <p class="mt-0.5 whitespace-pre-line">{{ document.blad }}</p>
          </div>
        </div>

        <div v-if="document.ostrzezenie" class="flex gap-3 rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3">
          <ExclamationIcon class="h-5 w-5 flex-shrink-0 text-yellow-500" aria-hidden="true" />
          <div class="text-sm text-yellow-800">
            <p class="font-semibold text-yellow-900">Uwaga</p>
            <p class="mt-0.5">{{ document.ostrzezenie }}</p>
          </div>
        </div>

        <!-- BRAKI na dzień dokumentu. Przy wystawionym wydaniu to pozycje, które NIE
             weszły na dokument (są w „Uwagach"). PW przyjmie je na stan, a wydanie
             uzupełniające wyda je po PW - o ile księgowość tego chce. -->
        <div v-if="document.braki && document.braki.length > 0" class="rounded-md border border-orange-200 bg-orange-50 px-4 py-3">
          <div class="flex gap-3">
            <ArchiveIcon class="h-5 w-5 flex-shrink-0 text-orange-500" aria-hidden="true" />
            <div class="min-w-0 flex-1 text-sm text-orange-800">
              <p class="font-semibold text-orange-900">
                {{ document.status === 'WYSTAWIONY'
                    ? `Na dzień ${documentDateText} zabrakło towaru — te sztuki nie weszły na dokument`
                    : `Na dzień ${documentDateText} brakuje towaru` }}
              </p>
              <div class="mt-2 flex flex-wrap gap-1.5">
                <!-- Brak z rezerwacji to co innego niż brak na półce: towar jest, ale Subiekt
                     trzyma go pod zamówienie. PW tu nie pomoże, więc oznaczamy go osobno. -->
                <span v-for="shortage in document.braki" :key="shortage.symbolTowaru"
                  v-tooltip="shortage.zarezerwowane > 0
                    ? `Zarezerwowane pod zamówienie od klienta: ${shortage.zarezerwowane} szt. — dodaj ręcznie w Subiekcie z opcją „pomiń rezerwację”.`
                    : `Dostępne na ${documentDateText}: ${shortage.stan}, potrzeba: ${shortage.potrzeba}`"
                  :class="[shortage.zarezerwowane > 0 ? 'text-purple-800 ring-purple-200' : 'text-orange-800 ring-orange-200',
                           'doc-tabular inline-flex items-center gap-1 rounded-md bg-white px-2 py-0.5 text-xs font-semibold ring-1 ring-inset']">
                  {{ shortage.symbolTowaru }} ×{{ Math.ceil(shortage.potrzeba - shortage.stan) }}
                  <span v-if="shortage.zarezerwowane > 0" class="font-medium text-purple-600">· rezerwacja</span>
                </span>
              </div>
              <p class="mt-2">
                <template v-if="document.status === 'WYSTAWIONY'">
                  Lista jest też w polu „Uwagi” dokumentu w Subiekcie. Jeśli ten towar ma jednak wejść na wydanie,
                  utwórz przyjęcie na braki (PW) — system przygotuje też wydanie uzupełniające, które pójdzie po PW.
                </template>
                <template v-else>
                  Subiekt wydaje tylko z dostaw sprzed daty dokumentu. Przyjęcie na braki (PW) wprowadzi brakujące sztuki na ten dzień.
                </template>
              </p>
              <div class="mt-3 flex flex-wrap items-center gap-3">
                <button v-if="canCreateReceipt" @click="createReceiptForShortage()" :disabled="tworzePW" type="button"
                  :class="[tworzePW ? 'opacity-50 pointer-events-none' : 'hover:bg-orange-200', 'transition duration-200 inline-flex h-8 items-center gap-1.5 rounded-md bg-orange-100 px-3 text-xs font-semibold text-orange-800']">
                  <RefreshIcon v-if="tworzePW" class="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  {{ document.pwBrakow ? 'Przelicz przyjęcie na braki' : (document.status === 'WYSTAWIONY' ? 'Utwórz PW i uzupełnienie wydania' : 'Utwórz przyjęcie na braki (PW)') }}
                </button>
                <a v-if="document.pwBrakow" @click="openDocument(document.pwBrakow.id)" class="cursor-pointer text-xs font-semibold text-orange-800 underline hover:text-orange-900">Otwórz PW na braki</a>
                <a v-if="document.uzupelnienie" @click="openDocument(document.uzupelnienie.id)" class="cursor-pointer text-xs font-semibold text-orange-800 underline hover:text-orange-900">Otwórz wydanie uzupełniające</a>
              </div>
            </div>
          </div>
        </div>

        <div v-if="hasBlockers && !document.tylkoPorownanie" class="flex gap-3 rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3">
          <ExclamationIcon class="h-5 w-5 flex-shrink-0 text-yellow-500" aria-hidden="true" />
          <div class="min-w-0 flex-1 text-sm text-yellow-800">
            <p class="font-semibold text-yellow-900">{{ document.blokady.length === 1 ? 'Przed wysłaniem uzupełnij' : `Przed wysłaniem uzupełnij (${document.blokady.length})` }}</p>
            <ul class="mt-1 list-disc space-y-0.5 pl-5">
              <li v-for="reason in document.blokady" :key="reason">{{ reason }}</li>
            </ul>
            <!-- Pozycja bez symbolu nie ma czego wskazać w Subiekcie. Zamiast blokować
                 cały dokument, można ją świadomie pominąć - zostaje widoczna, przekreślona. -->
            <button v-if="hasMissingSymbol && canEdit" @click="toggleSkip(false)" :disabled="savingPomin" type="button"
              :class="[savingPomin ? 'opacity-50 pointer-events-none' : 'hover:bg-yellow-200', 'mt-2 transition duration-200 inline-flex h-8 items-center gap-1.5 rounded-md bg-yellow-100 px-3 text-xs font-semibold text-yellow-800']">
              <RefreshIcon v-if="savingPomin" class="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              Pomiń pozycje bez symbolu i wyślij resztę
            </button>
          </div>
        </div>

        <div v-if="document.tylkoPorownanie" class="flex gap-3 rounded-md border border-gray-200 bg-white px-4 py-3">
          <InformationCircleIcon class="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
          <p class="text-sm text-gray-700">
            Ten dokument złożyła poprzednia wersja systemu jako zestawienie do porównania z fakturą — dlatego nie da się go
            ani edytować, ani wysłać. Po aktualizacji systemu na serwerze przebuduje się sam przy wejściu na ten dzień
            i będzie można wpisać numer faktury, ceny i wysłać PZ.
          </p>
        </div>
      </div>

      <div class="mt-4 space-y-4">
        <!-- Dane nagłówka nad pozycjami, w jednym rzędzie - pozycje dostają pełną szerokość. -->
        <div v-if="!document.tylkoPorownanie && (document.grupa === 'PZ_ZAKUP' || hasContractor)" class="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
          <!-- Numer faktury dostawcy - klucz do powiązania PZ z fakturą zakupu i KSeF. -->
          <div v-if="document.grupa === 'PZ_ZAKUP'" class="rounded-lg border bg-white px-4 py-4 shadow">
            <h2 class="text-sm font-semibold text-gray-900">Faktura dostawcy</h2>
            <p class="mt-0.5 text-xs text-gray-500">Trafi do Subiekta jako numer oryginalny PZ — po nim księgowość połączy PZ z fakturą zakupu.</p>
            <div class="mt-3 flex items-center gap-2">
              <div class="relative flex-1 rounded-md border border-gray-300 bg-white shadow-sm focus-within:border-indigo-600 focus-within:ring-1 focus-within:ring-indigo-600">
                <label for="numerFaktury" class="absolute -top-2 left-2 -mt-px inline-block rounded-t-md bg-gradient-to-t from-white to-gray-100 px-1 text-xs font-medium text-gray-900">Numer faktury</label>
                <input id="numerFaktury" v-model="invoiceNumber" :disabled="!canEdit" @keyup.enter="saveInvoiceNumber()" type="text" maxlength="30" placeholder="np. FV/123/09/2026"
                  class="block h-9 w-full border-0 bg-transparent px-3 text-sm focus:ring-0 disabled:text-gray-500" />
              </div>
              <button v-if="canEdit" @click="saveInvoiceNumber()" :disabled="savingFaktura || invoiceNumber.trim() === (document.numerFaktury || '')" type="button"
                :class="[(savingFaktura || invoiceNumber.trim() === (document.numerFaktury || '')) ? 'opacity-50 pointer-events-none' : 'hover:bg-indigo-200', 'transition-all duration-300 inline-flex h-9 items-center rounded-md bg-indigo-100 px-3 text-xs font-semibold text-indigo-600 shadow-sm']">
                Zapisz
              </button>
            </div>
          </div>

          <!-- Kontrahent: system tylko PROPONUJE, wysyłka wymaga potwierdzenia człowieka. -->
          <div v-if="hasContractor" class="rounded-lg border bg-white px-4 py-4 shadow">
            <div class="flex items-baseline justify-between gap-2">
              <h2 class="text-sm font-semibold text-gray-900">Kontrahent</h2>
              <router-link to="/dashboard/subiekt/ustawienia" class="text-xs font-semibold text-indigo-600 hover:text-indigo-500">Kartoteka</router-link>
            </div>

            <div v-if="document.kontrahent" :class="[document.kontrahent.potwierdzony ? 'bg-green-50' : 'bg-yellow-50', 'mt-3 rounded-md px-3 py-2.5']">
              <div class="flex flex-wrap items-center gap-2">
                <CheckCircleIcon v-if="document.kontrahent.potwierdzony" class="h-4 w-4 text-green-600" aria-hidden="true" />
                <ExclamationIcon v-else class="h-4 w-4 text-yellow-500" aria-hidden="true" />
                <span :class="[document.kontrahent.potwierdzony ? 'text-green-800' : 'text-yellow-800', 'text-sm font-semibold']">{{ document.kontrahent.nazwa || 'Symbol wpisany ręcznie' }}</span>
              </div>
              <p class="mt-1 font-mono text-xs text-gray-600">{{ document.kontrahent.symbol }}</p>
              <p :class="[document.kontrahent.potwierdzony ? 'text-green-700' : 'text-yellow-700', 'mt-1 text-xs']">
                <template v-if="!document.kontrahent.potwierdzony">Propozycja · dopasowanie {{ Math.round((document.kontrahent.pewnosc || 0) * 100) }}%. </template>
                {{ document.kontrahent.powod }}
                <template v-if="!document.kontrahent.zKartoteki"> Symbolu nie ma w kartotece.</template>
              </p>
              <button v-if="!document.kontrahent.potwierdzony && canEdit" @click="confirmProposal()" :disabled="savingKontrahent" type="button"
                :class="[savingKontrahent ? 'opacity-50 pointer-events-none' : 'hover:bg-yellow-200', 'mt-2 transition duration-200 inline-flex h-8 items-center rounded-md bg-yellow-100 px-3 text-xs font-semibold text-yellow-800']">
                Tak, to ten kontrahent
              </button>
            </div>
            <p v-else class="mt-3 rounded-md bg-gray-50 px-3 py-2.5 text-xs text-gray-600">System nie znalazł pasującego kontrahenta — wybierz go z kartoteki.</p>

            <div v-if="canEdit" class="mt-3 flex items-end gap-2">
              <Listbox v-model="selectedContractorId" as="div" class="relative flex-1">
                <ListboxButton class="relative h-9 w-full cursor-pointer rounded-md border border-gray-300 bg-white pl-3 pr-9 text-left text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                  <span v-if="selectedContractor" class="block truncate">{{ selectedContractor.nazwa }}</span>
                  <span v-else class="block truncate text-gray-500">{{ document.kontrahent ? 'Zmień kontrahenta…' : 'Wybierz z kartoteki…' }}</span>
                  <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <SelectorIcon class="h-4 w-4 text-gray-400" aria-hidden="true" />
                  </span>
                </ListboxButton>
                <ListboxOptions class="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <ListboxOption v-for="k in kontrahenciDoWyboru" :key="k.id" :value="k.id" v-slot="{ active, selected }" as="template">
                    <li :class="[active ? 'bg-indigo-50' : '', 'cursor-pointer px-3 py-2']">
                      <p :class="[selected ? 'font-semibold' : '', 'truncate text-sm text-gray-900']">{{ k.nazwa }}</p>
                      <p v-if="k.symbol !== k.nazwa" class="mt-0.5 truncate font-mono text-xs text-gray-500">{{ k.symbol }}</p>
                    </li>
                  </ListboxOption>
                  <li v-if="kontrahenciDoWyboru.length === 0" class="px-3 py-2 text-sm text-gray-500">Brak innych kontrahentów w kartotece.</li>
                </ListboxOptions>
              </Listbox>
              <button @click="saveKontrahent()" :disabled="savingKontrahent || selectedContractorId.length === 0" type="button"
                :class="[(savingKontrahent || selectedContractorId.length === 0) ? 'opacity-50 pointer-events-none' : 'hover:bg-indigo-200', 'transition-all duration-300 inline-flex h-9 items-center rounded-md bg-indigo-100 px-3 text-xs font-semibold text-indigo-600 shadow-sm']">
                Ustaw
              </button>
            </div>
          </div>

        </div>
        <!-- POZYCJE -->
        <div class="min-w-0">
          <div class="overflow-hidden rounded-lg border bg-white shadow">
            <div class="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-3 sm:px-6">
              <h2 class="text-sm font-semibold text-gray-900">
                Pozycje <span class="ml-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600 doc-tabular">{{ document.towary ? document.towary.length : 0 }}</span>
              </h2>
              <button v-if="skippedCount > 0 && canEdit" @click="toggleSkip(true)" :disabled="savingPomin" type="button"
                :class="[savingPomin ? 'opacity-50 pointer-events-none' : '', 'text-xs font-semibold text-indigo-600 transition duration-200 hover:text-indigo-500']">
                Przywróć pominięte ({{ skippedCount }})
              </button>
            </div>

            <div class="doc-positions overflow-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="doc-sticky-head bg-gray-50">
                  <tr>
                    <th scope="col" class="py-3 pl-4 pr-3 text-left text-xs font-semibold uppercase text-gray-600 sm:pl-6">Symbol</th>
                    <th scope="col" class="w-1/3 px-3 py-3 text-left text-xs font-semibold uppercase text-gray-600">Nazwa</th>
                    <th v-if="pokazOpis" scope="col" class="px-3 py-3 text-left text-xs font-semibold uppercase text-gray-600">Klient</th>
                    <th v-if="pokazZamowienie" scope="col" class="px-3 py-3 text-left text-xs font-semibold uppercase text-gray-600">Trasa / zamówienie</th>
                    <th scope="col" class="px-3 py-3 text-right text-xs font-semibold uppercase text-gray-600">{{ pokazPorownanieIlosci ? 'Przyjęto' : 'Ilość' }}</th>
                    <th v-if="pokazPorownanieIlosci" scope="col" class="px-3 py-3 text-right text-xs font-semibold uppercase text-gray-600">Z faktury</th>
                    <th v-if="pokazPorownanieIlosci" scope="col" class="px-3 py-3 text-right text-xs font-semibold uppercase text-gray-600">Różnica</th>
                    <th v-if="pokazKwoty" scope="col" class="px-3 py-3 text-right text-xs font-semibold uppercase text-gray-600">Cena netto</th>
                    <th v-if="pokazKwoty" scope="col" class="px-3 py-3 text-right text-xs font-semibold uppercase text-gray-600">Wartość</th>
                    <th v-if="canEdit" scope="col" class="py-3 pl-3 pr-4 sm:pr-6"><span class="sr-only">Czynności</span></th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-100 bg-white">
                  <!-- Klucz obejmuje zamówienie: ten sam mebel stoi w dokumencie raz na klienta. -->
                  <tr v-for="item in sortedItems" :key="rowKey(item)"
                    :class="[item.pominieta ? 'opacity-50' : '', missingKeys.has(rowKey(item)) ? 'bg-orange-50' : '', 'transition duration-200 hover:bg-gray-50']">
                    <td class="whitespace-nowrap py-3 pl-4 pr-3 align-top sm:pl-6">
                      <a v-if="item.brakSymbolu" :href="`/dashboard/products/list/editProduct?id=${item.productId}`" target="_blank"
                        class="rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-800 underline transition duration-200 hover:bg-red-200">brak symbolu — uzupełnij</a>
                      <span v-else class="font-mono text-xs font-semibold text-gray-700">{{ item.symbolTowaru }}</span>
                    </td>
                    <td class="px-3 py-3 align-top">
                      <p :class="[item.pominieta ? 'line-through' : '', 'text-xs font-semibold text-gray-900']">{{ item.nazwaProduktu || item.nazwaTowaru }}</p>
                      <p v-if="item.nazwaWariantu" class="mt-0.5 text-xs text-gray-500">{{ item.nazwaWariantu }}</p>
                      <span v-if="item.pominieta" class="mt-1 inline-flex rounded bg-gray-200 px-1.5 py-0.5 text-xs font-medium text-gray-600">pominięta</span>
                      <span v-if="missingKeys.has(rowKey(item))" class="mt-1 inline-flex rounded bg-orange-100 px-1.5 py-0.5 text-xs font-medium text-orange-800">
                        nie weszło: {{ missingKeys.get(rowKey(item)) }} szt
                      </span>
                    </td>
                    <td v-if="pokazOpis" class="px-3 py-3 align-top text-xs text-gray-600">{{ item.opis || '—' }}</td>
                    <td v-if="pokazZamowienie" class="px-3 py-3 align-top text-xs">
                      <p v-if="item.trasa" class="font-semibold text-gray-600">{{ item.trasa }}</p>
                      <p class="mt-0.5 flex flex-wrap items-center gap-x-2">
                        <span v-if="item.orderId" class="font-mono text-gray-400">{{ item.orderId }}</span>
                        <span v-if="item.kanalSurowy && item.kanalSurowy.length" class="rounded bg-gray-100 px-1.5 py-0.5 font-medium text-gray-600">{{ item.kanalSurowy.join(' · ') }}</span>
                      </p>
                    </td>
                    <td class="whitespace-nowrap px-3 py-3 text-right align-top text-xs font-semibold text-gray-700 doc-tabular">{{ item.iloscJm }} szt</td>
                    <td v-if="pokazPorownanieIlosci" class="px-3 py-2 text-right align-top">
                      <input v-if="canEdit" v-model.number="item.iloscFaktury" type="number" min="0" step="1" placeholder="—"
                        class="w-20 rounded-md border-gray-300 px-2 py-1 text-right text-xs doc-tabular focus:border-indigo-500 focus:outline-none focus:ring-indigo-500" />
                      <span v-else class="text-xs text-gray-700 doc-tabular">{{ item.iloscFaktury == null ? '—' : item.iloscFaktury }}</span>
                    </td>
                    <td v-if="pokazPorownanieIlosci" class="whitespace-nowrap px-3 py-3 text-right align-top">
                      <span v-if="roznicaIlosci(item) === null" class="text-xs text-gray-400">—</span>
                      <span v-else-if="roznicaIlosci(item) === 0" class="rounded bg-green-50 px-1.5 py-0.5 text-xs font-medium text-green-700">zgadza się</span>
                      <span v-else class="rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800 doc-tabular">
                        {{ roznicaIlosci(item) > 0 ? 'brakuje ' + roznicaIlosci(item) : 'nadmiar ' + (-roznicaIlosci(item)) }} szt
                      </span>
                    </td>
                    <td v-if="pokazKwoty" class="px-3 py-2 text-right align-top">
                      <input v-if="canEdit" v-model.number="item.cenaNetto" type="number" min="0" step="0.01" placeholder="0,00"
                        class="w-24 rounded-md border-gray-300 px-2 py-1 text-right text-xs doc-tabular focus:border-indigo-500 focus:outline-none focus:ring-indigo-500" />
                      <span v-else class="text-xs text-gray-700 doc-tabular">{{ formatMoney(item.cenaNetto || 0) }}</span>
                    </td>
                    <td v-if="pokazKwoty" class="whitespace-nowrap px-3 py-3 text-right align-top text-xs text-gray-700 doc-tabular">
                      {{ formatMoney((Number(item.cenaNetto) || 0) * Number(item.iloscJm || 0)) }}
                    </td>
                    <td v-if="canEdit" class="whitespace-nowrap py-3 pl-3 pr-4 text-right align-top sm:pr-6">
                      <button @click="toggleSkip(item.pominieta, rowKey(item))" :disabled="savingPomin" type="button"
                        :class="[savingPomin ? 'opacity-50 pointer-events-none' : 'hover:bg-gray-100 hover:text-gray-700', 'rounded px-1.5 py-0.5 text-xs font-medium text-gray-500 transition duration-200']">
                        {{ item.pominieta ? 'przywróć' : 'pomiń' }}
                      </button>
                    </td>
                  </tr>
                </tbody>
                <tfoot class="doc-sticky-foot">
                  <tr>
                    <td :colspan="2 + (pokazOpis ? 1 : 0) + (pokazZamowienie ? 1 : 0)" class="py-3 pl-4 pr-3 text-xs font-semibold uppercase text-gray-600 sm:pl-6">Razem</td>
                    <td class="whitespace-nowrap px-3 py-3 text-right text-xs font-semibold text-gray-900 doc-tabular">{{ totalQuantity }} szt</td>
                    <td v-if="pokazPorownanieIlosci" class="whitespace-nowrap px-3 py-3 text-right text-xs font-semibold text-gray-900 doc-tabular">{{ invoiceTotal == null ? '—' : invoiceTotal + ' szt' }}</td>
                    <td v-if="pokazPorownanieIlosci"></td>
                    <td v-if="pokazKwoty"></td>
                    <td v-if="pokazKwoty" class="whitespace-nowrap px-3 py-3 text-right text-xs font-semibold text-gray-900 doc-tabular">{{ formatMoney(netTotal) }}</td>
                    <td v-if="canEdit"></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <!-- Kwoty i zgodność z fakturą - tylko przy przyjęciach. -->
            <div v-if="pokazKwoty" class="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-end sm:px-6">
              <div v-if="pokazPorownanieIlosci" class="flex flex-wrap items-center gap-2 text-xs">
                <span class="inline-flex h-8 items-center rounded-md bg-white px-2.5 font-medium text-gray-700 ring-1 ring-inset ring-gray-200">
                  sprawdzono {{ podsumowaniePorownania.sprawdzone }} z {{ podsumowaniePorownania.wszystkie }}
                </span>
                <span v-if="podsumowaniePorownania.zgodne > 0" class="inline-flex h-8 items-center gap-1 rounded-md bg-green-50 px-2.5 font-medium text-green-800">
                  <CheckCircleIcon class="h-4 w-4" aria-hidden="true" />{{ podsumowaniePorownania.zgodne }} zgodnych
                </span>
                <span v-if="podsumowaniePorownania.rozne > 0" class="inline-flex h-8 items-center gap-1 rounded-md bg-red-50 px-2.5 font-medium text-red-800">
                  <ExclamationIcon class="h-4 w-4" aria-hidden="true" />{{ podsumowaniePorownania.rozne }} różnych
                </span>
              </div>
              <div class="relative rounded-md border border-gray-300 bg-white shadow-sm focus-within:border-indigo-600 focus-within:ring-1 focus-within:ring-indigo-600 sm:ml-auto">
                <label for="wartoscFaktury" class="absolute -top-2 left-2 -mt-px inline-block rounded-t-md bg-gradient-to-t from-white to-gray-100 px-1 text-xs font-medium text-gray-900">Wartość netto z faktury</label>
                <input v-if="canEdit" id="wartoscFaktury" v-model.number="invoiceValue" type="number" min="0" step="0.01" placeholder="0,00"
                  class="block h-9 w-44 border-0 bg-transparent px-3 text-right text-sm doc-tabular focus:ring-0" />
                <p v-else class="flex h-9 w-44 items-center justify-end px-3 text-sm text-gray-700 doc-tabular">{{ invoiceValue == null ? '—' : formatMoney(invoiceValue) }}</p>
              </div>
              <p v-if="roznica !== null && Math.abs(roznica) < 0.005" class="inline-flex h-9 items-center gap-1.5 rounded-md bg-green-50 px-3 text-xs font-semibold text-green-800">
                <CheckCircleIcon class="h-4 w-4" aria-hidden="true" />Zgadza się z fakturą
              </p>
              <p v-else-if="roznica !== null" class="inline-flex h-9 items-center gap-1.5 rounded-md bg-red-50 px-3 text-xs font-semibold text-red-800">
                <ExclamationIcon class="h-4 w-4" aria-hidden="true" />
                {{ roznica < 0 ? 'Mniej' : 'Więcej' }} o {{ formatMoney(Math.abs(roznica)) }} niż na fakturze
              </p>
              <button v-if="canEdit" @click="saveCeny()" :disabled="savingCeny" type="button"
                :class="[savingCeny ? 'opacity-50 pointer-events-none' : 'hover:bg-indigo-200', 'transition-all duration-300 inline-flex h-9 items-center gap-1.5 rounded-md bg-indigo-100 px-3 text-xs font-semibold text-indigo-600 shadow-sm']">
                <RefreshIcon v-if="savingCeny" class="h-4 w-4 animate-spin" aria-hidden="true" />
                {{ pokazPorownanieIlosci ? 'Zapisz kwoty i ilości' : 'Zapisz kwoty' }}
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { subiektPost } from '@/subiektApi';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue';
import {
  ExclamationIcon, RefreshIcon, CheckCircleIcon, ArchiveIcon, InformationCircleIcon,
  XCircleIcon, SelectorIcon, PaperAirplaneIcon,
} from '@heroicons/vue/solid';
import BridgeStatus from '@/components/SubiektBridgeStatus.vue';
import SubiektBreadcrumb from '@/components/SubiektBreadcrumb.vue';
import { subiektStatus, typeBadge } from '@/subiektUi';
import { invalidateDocuments } from '@/subiektDocumentsCache';

// Vue przy skasowanym polu type="number" oddaje PUSTY STRING, a nie null - "!= null"
// przepuszczało taki string i wyczyszczona pozycja liczyła się jako sprawdzona.
function maIloscFaktury(item) {
  return item.iloscFaktury != null && item.iloscFaktury !== '';
}

const GROUP_LABELS = {
  WZ: 'Wydanie na trasy',
  WZ_FAKTURA: 'Wydanie fakturowane',
  WZ_UZUPELNIENIE: 'Uzupełnienie wydania',
  PW_BRAKI: 'Przyjęcie na braki',
  PZ_ZAKUP: 'Przyjęcie od dostawcy',
  PZ_ZWROT: 'Zwrot / reklamacja',
  FZ_ZAKUP: 'Faktura zakupu',
  PRODUKCJA: 'Produkcja',
};

export default {
  name: 'SubiektDocumentDetails',
  components: {
    Listbox, ListboxButton, ListboxOptions, ListboxOption, BridgeStatus, SubiektBreadcrumb,
    ExclamationIcon, RefreshIcon, CheckCircleIcon, ArchiveIcon, InformationCircleIcon,
    XCircleIcon, SelectorIcon, PaperAirplaneIcon,
  },
  data() {
    return {
      loading: true,
      loadError: '',
      document: null,
      sendingEnabled: null,
      bridge: null,
      warehouses: {},
      kontrahenci: [],
      invoiceValue: null,
      invoiceNumber: '',
      manualNumber: '',
      selectedContractorId: '',
      sending: false,
      savingCeny: false,
      savingPomin: false,
      savingCofnij: false,
      savingPotwierdz: false,
      savingKontrahent: false,
      savingFaktura: false,
      tworzePW: false,
      tworzeFZ: false,
      refreshing: false,
      requestSeq: 0,
      ostatnieOdswiezenie: 0,
      pollTimer: null,
    };
  },
  computed: {
    documentId() {
      return (this.$route.query.id || '').toString().trim();
    },
    status() {
      return subiektStatus(this.document);
    },
    groupLabel() {
      return GROUP_LABELS[this.document?.grupa] || this.document?.action || '';
    },
    documentDateText() {
      const unix = Number(this.document?.documentDateUnix);
      if (unix > 0) return moment.unix(unix).format('DD.MM.YYYY');
      return this.document?.dzien ? moment(this.document.dzien, 'YYYY-MM-DD').format('DD.MM.YYYY') : '—';
    },
    // Pozycje wymagające uwagi idą na górę. Sortujemy KOPIĘ - kolejność w towary
    // to kolejność pozycji w Subiekcie.
    sortedItems() {
      const items = Array.isArray(this.document?.towary) ? this.document.towary : [];
      const weight = (item) => (item?.brakSymbolu ? 0 : (item?.pominieta ? 1 : 2));
      return items
        .map((item, index) => ({ item, index }))
        .sort((a, b) => weight(a.item) - weight(b.item) || a.index - b.index)
        .map(entry => entry.item);
    },
    // Pozycje, które most pominął przez brak na dzień dokumentu.
    missingKeys() {
      const map = new Map();
      for (const entry of (this.document?.brakiPozycji || [])) map.set(entry.klucz, entry.brakuje);
      return map;
    },
    hasBlockers() {
      return Array.isArray(this.document?.blokady) && this.document.blokady.length > 0;
    },
    hasContractor() {
      return ['WZ', 'PZ'].includes(this.document?.action) && !this.document?.tylkoPorownanie;
    },
    pokazKwoty() {
      return this.document?.action === 'PZ';
    },
    pokazPorownanieIlosci() {
      return this.document?.action === 'PZ'
        && (this.document?.tylkoPorownanie === true || this.document?.grupa === 'PZ_ZAKUP');
    },
    pokazZamowienie() {
      return (this.document?.towary || []).some(item => item.orderId || item.trasa);
    },
    pokazOpis() {
      return (this.document?.towary || []).some(item => item.opis);
    },
    skippedCount() {
      return (this.document?.towary || []).filter(item => item.pominieta === true).length;
    },
    kontrahenciDoWyboru() {
      const current = this.document?.kontrahent?.kontrahentId;
      return current ? this.kontrahenci.filter(k => k.id !== current) : this.kontrahenci;
    },
    selectedContractor() {
      return this.selectedContractorId ? (this.kontrahenciDoWyboru.find(k => k.id === this.selectedContractorId) || null) : null;
    },
    invoiceTotal() {
      const entered = (this.document?.towary || []).filter(item => maIloscFaktury(item));
      if (entered.length === 0) return null;
      return entered.reduce((total, item) => total + Number(item.iloscFaktury || 0), 0);
    },
    podsumowaniePorownania() {
      const items = this.document?.towary || [];
      const checked = items.filter(item => maIloscFaktury(item));
      const different = checked.filter(item => Number(item.iloscFaktury) !== Number(item.iloscJm));
      return { wszystkie: items.length, sprawdzone: checked.length, rozne: different.length, zgodne: checked.length - different.length };
    },
    // Dokument, który opuścił panel, jest zapisem tego, co poszło do Subiekta.
    // Stare zestawienie „do porównania" jest tylko do odczytu - nic z niego nie wyjdzie.
    canEdit() {
      return ['SZKIC', 'DO_WYSTAWIENIA', 'BLAD'].includes(this.document?.status) && !this.document?.tylkoPorownanie;
    },
    canSend() {
      return ['SZKIC', 'DO_WYSTAWIENIA', 'BLAD'].includes(this.document?.status);
    },
    canRevert() {
      return ['WYSLANY', 'W_TRAKCIE'].includes(this.document?.status);
    },
    // PW na braki: po nieudanym wydaniu (szkic z brakami) albo po wydaniu, które
    // powstało bez brakujących pozycji.
    // Czy jest co przyjąć na PW - braki z rezerwacji się nie liczą (towar fizycznie jest).
    hasStockShortage() {
      return (this.document?.braki || []).some(item =>
        Math.ceil(Number(item.potrzeba) - Number(item.stan) - Number(item.zarezerwowane || 0)) > 0);
    },
    canCreateReceipt() {
      if (!this.hasStockShortage) return false;
      return this.canEdit || (this.document?.status === 'WYSTAWIONY' && this.document?.action === 'WZ');
    },
    isLegacyQueued() {
      return this.document?.status === 'WYSLANY' && this.document?.zadanie?.klucz == null;
    },
    queueTitle() {
      if (this.isLegacyQueued) return 'Wysłany poprzednią wersją mostu';
      return this.document?.status === 'W_TRAKCIE' ? 'Most wystawia dokument w Subiekcie' : 'Dokument czeka w kolejce';
    },
    queueText() {
      if (this.isLegacyQueued) {
        return 'Stary most nie odsyła wyniku. Jeśli dokument jest w Subiekcie — wpisz jego numer. Jeśli nie — cofnij i wyślij ponownie.';
      }
      if (this.bridge && !this.bridge.online) {
        return 'Most nie odzywa się — dokument pójdzie, gdy usługa mostu wróci.';
      }
      const attempts = Number(this.document?.zadanie?.proby || 0);
      return attempts > 0
        ? `Poprzednia próba nie przeszła — most ponowi ją automatycznie (próba ${attempts + 1} z 5).`
        : 'Most pobierze go w ciągu minuty. Strona odświeży się sama.';
    },
    netTotal() {
      return (this.document?.towary || []).reduce((total, item) => total + (Number(item.cenaNetto) || 0) * Number(item.iloscJm || 0), 0);
    },
    roznica() {
      if (this.invoiceValue == null || this.invoiceValue === '') return null;
      return this.netTotal - Number(this.invoiceValue);
    },
    totalQuantity() {
      return (this.document?.towary || []).reduce((sum, item) => sum + Number(item.iloscJm || 0), 0);
    },
    headerFields() {
      const doc = this.document;
      if (!doc) return [];
      const fields = [
        { label: doc.action === 'WZ' ? 'Data (wyjazd)' : 'Data', value: this.documentDateText },
        { label: 'Magazyn', value: this.warehouseName(doc.werehouseId) },
      ];
      if (doc.destinationWerehouseId) fields.push({ label: 'Magazyn docelowy', value: this.warehouseName(doc.destinationWerehouseId) });
      if (this.hasContractor) fields.push({ label: 'Kontrahent', value: doc.symbolKontrahenta });
      if (doc.naPodstawie) fields.push({ label: 'Na podstawie', value: doc.naPodstawie.numer });
      fields.push({ label: doc.action === 'FZ' ? 'Numer faktury dostawcy' : 'Numer oryginalny', value: doc.numerOryginalny });
      fields.push({ label: 'Tytuł', value: doc.opis, long: true });
      // Do „Uwag" most dopisze jeszcze linijkę „BRAK NA dd.mm: …", jeśli czegoś zabraknie.
      fields.push({ label: 'Uwagi', value: doc.uwagi, long: true });
      return fields;
    },
  },
  methods: {
    typeBadge,
    roznicaIlosci(item) {
      if (!maIloscFaktury(item)) return null;
      return Number(item.iloscFaktury) - Number(item.iloscJm || 0);
    },
    warehouseName(number) {
      if (number == null || number === '') return '—';
      const label = this.warehouses[String(number)];
      return label ? `${number} · ${label}` : String(number);
    },
    // Ten sam klucz co positionKey po stronie funkcji: wariant + zamówienie.
    rowKey(item) {
      const variant = item.productId && item.variantId ? `${item.productId}|${item.variantId}` : (item.symbolTowaru || item.nazwaTowaru || '');
      return item.orderId ? `${variant}|${item.orderId}` : variant;
    },
    formatMoney(value) {
      return `${(Math.round((Number(value) || 0) * 100) / 100).toFixed(2)} zł`;
    },
    formatUnix(unix) {
      return moment.unix(unix).format('DD.MM.YYYY, HH:mm');
    },
    goBack() {
      this.$router.push('/dashboard/subiekt/dokumenty');
    },
    openDocument(id) {
      this.$router.push({ path: '/dashboard/subiekt/dokumenty', query: { id } });
    },
    notify(success, head, subheader) {
      this.$store.commit('setNotification', { show: true, head, subheader: subheader || '', success });
    },
    errorText(error, fallback) {
      return typeof error === 'string' ? error : fallback;
    },
    stopPolling() {
      if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
    },
    // Wspólna obsługa akcji: wywołanie, komunikat, unieważnienie listy dnia i przeładowanie.
    async runAction(flag, path, payload, okHead, failHead, reloadSilently = false) {
      if (this[flag]) return false;
      this[flag] = true;
      try {
        const res = await subiektPost(this.$store.state.apiLink, path, { ...payload, user: this.$store.state.userData });
        if (!res.data.success) throw res.data.log || res.data.error;
        this.notify(true, okHead, res.data.log);
        invalidateDocuments(this.document.dzien);
        await this.loadDocument(reloadSilently);
        return true;
      } catch (error) {
        console.log(error);
        this.notify(false, failHead, this.errorText(error, 'Coś poszło nie tak.'));
        return false;
      } finally {
        this[flag] = false;
      }
    },
    async loadDocument(silent = false) {
      if (this.documentId.length === 0) {
        this.loading = false;
        this.loadError = 'Brak identyfikatora dokumentu w adresie.';
        return;
      }
      // Odpowiedź na STARY dokument nie może nadpisać nowego (odpytywanie, powrót
      // na kartę i zmiana ?id= potrafią być w locie naraz).
      const requestSeq = ++this.requestSeq;
      const id = this.documentId;
      const current = () => this.requestSeq === requestSeq && this.documentId === id;

      if (!silent) this.loading = true;
      this.loadError = '';
      try {
        const res = await subiektPost(this.$store.state.apiLink, '/subiekt/document', { id });
        if (!res.data.success) throw res.data.log || res.data.error;
        if (!current()) return;
        this.document = res.data.dokument;
        this.invoiceValue = this.document?.wartoscFaktury ?? null;
        this.invoiceNumber = this.document?.numerFaktury || '';
        this.sendingEnabled = res.data.wysylkaWlaczona === true;
        this.bridge = res.data.most || null;
        this.warehouses = res.data.magazyny || {};
      } catch (error) {
        console.log(error);
        if (!silent && current()) this.loadError = this.errorText(error, 'Nie udało się wczytać dokumentu.');
      } finally {
        if (!silent && current()) this.loading = false;
      }
    },
    async loadKontrahenci() {
      try {
        const res = await subiektPost(this.$store.state.apiLink, '/subiekt/kontrahenci', {});
        if (res.data.success) this.kontrahenci = res.data.kontrahenci || [];
      } catch (error) {
        console.log(error);
      }
    },
    confirmProposal() {
      const id = this.document?.kontrahent?.kontrahentId;
      if (id) this.applyKontrahent(id);
    },
    saveKontrahent() {
      if (this.selectedContractorId) this.applyKontrahent(this.selectedContractorId);
    },
    async applyKontrahent(kontrahentId) {
      const ok = await this.runAction('savingKontrahent', '/subiekt/document/kontrahent',
        { id: this.document.id, kontrahentId }, 'Kontrahent ustawiony.', 'Nie udało się ustawić kontrahenta.');
      if (ok) this.selectedContractorId = '';
    },
    saveInvoiceNumber() {
      return this.runAction('savingFaktura', '/subiekt/document/faktura',
        { id: this.document.id, numerFaktury: this.invoiceNumber.trim() }, 'Zapisano numer faktury.', 'Nie udało się zapisać numeru faktury.', true);
    },
    async confirmManually() {
      const number = this.manualNumber.trim();
      if (number.length === 0) return;
      const ok = await this.runAction('savingPotwierdz', '/subiekt/document/potwierdz',
        { id: this.document.id, numerDokumentu: number }, 'Oznaczono jako wystawiony.', 'Nie udało się potwierdzić.');
      if (ok) this.manualNumber = '';
    },
    cofnij() {
      const number = this.document?.subiekt?.numer;
      let question;
      if (this.document?.status === 'WYSTAWIONY') {
        question = `Ten dokument JEST w Subiekcie${number ? ' jako ' + number : ''}. Cofnięcie pozwoli wysłać go ponownie i powstanie DRUGI dokument, chyba że najpierw usuniesz tamten. Na pewno?`;
      } else if (this.document?.status === 'WYSLANY' && !this.isLegacyQueued) {
        question = 'Wycofać dokument z kolejki? Wróci do szkicu.';
      } else {
        question = 'Cofnąć dokument do szkicu? Najpierw sprawdź w Subiekcie, czy nie powstał — inaczej zrobi się duplikat.';
      }
      if (!window.confirm(question)) return;
      return this.runAction('savingCofnij', '/subiekt/document/cofnij', { id: this.document.id }, 'Dokument cofnięty.', 'Nie udało się cofnąć.');
    },
    // Symbol Subiekta jest ZAPISANY w dokumencie - uzupełnienie go w kartotece
    // produktu nie zmienia nic, dopóki dokument nie zostanie przebudowany.
    // Przebudowuje się DOKŁADNIE TEN dokument, z jego własnych tras.
    async refreshFromCatalog(silent = false) {
      if (this.refreshing || !this.document?.dzien) return;
      const routeIds = Array.isArray(this.document.trasy) ? this.document.trasy : null;
      if (routeIds === null && this.document.action === 'WZ') {
        if (!silent) this.notify(false, 'Nie można odświeżyć tego wydania.', 'Powstało przed zapisywaniem listy tras — utwórz je od nowa z tras.');
        return;
      }

      this.refreshing = true;
      this.ostatnieOdswiezenie = Date.now();
      try {
        const res = await subiektPost(this.$store.state.apiLink, '/subiekt/documents', {
          date: this.document.dzien,
          routes: routeIds || [],
          rebuildDocumentId: this.document.id,
        });
        if (!res.data.success) throw res.data.log || res.data.error;
        invalidateDocuments(this.document.dzien);
        await this.loadDocument(true);
        if (!silent) {
          const missing = (this.document?.towary || []).some(item => item.brakSymbolu && !item.pominieta);
          this.notify(!missing, missing ? 'Odświeżono — nadal brakuje symboli.' : 'Odświeżono z kartoteki.');
        }
      } catch (error) {
        console.log(error);
        if (!silent) this.notify(false, 'Nie udało się odświeżyć.', this.errorText(error, 'Coś poszło nie tak.'));
      } finally {
        this.refreshing = false;
      }
    },
    // Symbol uzupełnia się w osobnej karcie - powrót tutaj to dobry moment na
    // przebudowę. Odstęp czasowy chroni przed przebudową przy każdym przełączeniu.
    onTabReturn() {
      if (document.visibilityState !== 'visible' || !this.canEdit) return;
      if (Date.now() - this.ostatnieOdswiezenie < 15000) return;
      this.refreshFromCatalog(true);
    },
    createPurchaseInvoice() {
      return this.runAction('tworzeFZ', '/subiekt/document/fz', { id: this.document.id },
        'Faktura zakupu przygotowana.', 'Nie udało się przygotować faktury zakupu.');
    },
    createReceiptForShortage() {
      const list = (this.document?.braki || [])
        .map(item => `${item.symbolTowaru} ×${Math.ceil(Number(item.potrzeba) - Number(item.stan))}`).join(', ');
      const supplement = this.document?.status === 'WYSTAWIONY'
        ? '\n\nPowstanie też szkic wydania uzupełniającego z tymi pozycjami — most wystawi je dopiero po PW.'
        : '';
      if (!window.confirm(`Utworzyć przyjęcie wewnętrzne (PW) na: ${list}?\n\nWycena z kartoteki towaru. PW powstanie jako szkic do wysłania.${supplement}`)) return;
      return this.runAction('tworzePW', '/subiekt/document/pw', { documentId: this.document.id },
        'Utworzono przyjęcie na braki.', 'Nie udało się utworzyć przyjęcia.');
    },
    // Bez klucza działa hurtem: pomija wszystkie bez symbolu albo przywraca wszystkie pominięte.
    toggleSkip(restoreAll, key) {
      return this.runAction('savingPomin', '/subiekt/document/pomin',
        { id: this.document.id, przywroc: restoreAll, klucz: key || '' }, 'Zaktualizowano pozycje.', 'Nie udało się zmienić pozycji.');
    },
    saveCeny() {
      return this.runAction('savingCeny', '/subiekt/document/ceny', {
        id: this.document.id,
        ceny: (this.document.towary || []).map(item => {
          const entry = { klucz: this.rowKey(item), cenaNetto: item.cenaNetto };
          // Klucza ilości NIE dokładamy poza porównaniem - backend odróżnia
          // „pola nie było" od „pole jest puste".
          if (this.pokazPorownanieIlosci) entry.iloscFaktury = maIloscFaktury(item) ? Number(item.iloscFaktury) : null;
          return entry;
        }),
        wartoscFaktury: this.invoiceValue,
      }, 'Zapisano.', 'Nie udało się zapisać kwot.', true);
    },
    sendDocument() {
      return this.runAction('sending', '/subiekt/send', { id: this.document.id }, 'Dokument w kolejce do Subiekta.', 'Nie udało się wysłać dokumentu.');
    },
  },
  watch: {
    '$route.query.id'() {
      if (this.documentId) this.loadDocument();
    },
    // Dopóki dokument czeka na most, dopytujemy co 8 s.
    'document.status': {
      immediate: true,
      handler(status) {
        this.stopPolling();
        if (status === 'WYSLANY' || status === 'W_TRAKCIE') this.pollTimer = setInterval(() => this.loadDocument(true), 8000);
      },
    },
  },
  created() {
    moment.locale('pl');
  },
  mounted() {
    this.loadDocument();
    this.loadKontrahenci();
    document.addEventListener('visibilitychange', this.onTabReturn);
    window.addEventListener('focus', this.onTabReturn);
  },
  beforeUnmount() {
    this.stopPolling();
    document.removeEventListener('visibilitychange', this.onTabReturn);
    window.removeEventListener('focus', this.onTabReturn);
  },
};
</script>

<style scoped>
/* Tailwind 2 bez JIT nie generuje max-h-[60vh] - wysokość trzymamy tutaj. */
.doc-positions {
  max-height: 65vh;
}
/* Nagłówek i suma zostają w kadrze przy przewijaniu długiego wydania. */
.doc-sticky-head th {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: #f9fafb;
}
.doc-sticky-foot td {
  position: sticky;
  bottom: 0;
  z-index: 1;
  background-color: #f9fafb;
  border-top: 1px solid #e5e7eb;
}
.doc-tabular {
  font-variant-numeric: tabular-nums;
}
</style>
